import { forwardRef, useMemo, useState } from 'react'
import {Pizza} from 'lucide-react'
import SolverCard from '@/components/SolverCard'
import CopyButton from '@/components/CopyButton'
import EmailSolutionButton from '@/components/EmailSolutionButton'
import { gcd } from '@/lib/calculators'

interface MixedFraction {
  whole: number
  num: number
  den: number
}

type Op = '+' | '-' | '*' | '/'

function toImproper(f: MixedFraction): { num: number, den: number } {
  const sign = f.whole < 0 || (f.whole === 0 && f.num < 0) ? -1 : 1
  const w = Math.abs(f.whole)
  const n = Math.abs(f.num)
  const d = f.den === 0 ? 1 : Math.abs(f.den)
  return { num: sign * (w * d + n), den: d }
}

function compute(a: MixedFraction, b: MixedFraction, op: Op) {
  const A = toImproper(a)
  const B = toImproper(b)
  let num = 0
  let den = 1
  if (op === '+') { num = A.num * B.den + B.num * A.den; den = A.den * B.den }
  else if (op === '-') { num = A.num * B.den - B.num * A.den; den = A.den * B.den }
  else if (op === '*') { num = A.num * B.num; den = A.den * B.den }
  else { num = A.num * B.den; den = A.den * B.num }
  if (den === 0) return { A, B, num: 0, den: 1, simpNum: 0, simpDen: 1, g: 1, mixed: { whole: 0, num: 0, den: 1 }, valid: false }
  const sign = (num < 0) !== (den < 0) ? -1 : 1
  num = Math.abs(num); den = Math.abs(den)
  const g = gcd(num, den)
  const simpNum = sign * (num / g)
  const simpDen = den / g
  const whole = Math.trunc(simpNum / simpDen)
  const remNum = Math.abs(simpNum) - Math.abs(whole) * simpDen
  return {
    A, B, num: sign * num, den, simpNum, simpDen, g,
    mixed: { whole, num: remNum, den: simpDen },
    valid: true,
  }
}

interface Props { highlighted: boolean }

const FractionCalculator = forwardRef<HTMLElement, Props>(({ highlighted }, ref) => {
  const [a, setA] = useState<MixedFraction>({ whole: 1, num: 1, den: 2 })
  const [b, setB] = useState<MixedFraction>({ whole: 0, num: 2, den: 3 })
  const [op, setOp] = useState<Op>('+')

  const r = useMemo(() => compute(a, b, op), [a, b, op])

  const pieValue = r.valid && r.simpDen > 0 ? r.simpNum / r.simpDen : 0
  const slices = Math.max(1, Math.min(36, r.simpDen))
  const filledFloat = Math.max(0, pieValue) * slices
  const fullFilled = Math.floor(filledFloat)
  const partial = filledFloat - fullFilled

  const md = useMemo(() => {
    if (!r.valid) return '# Mixed Fraction\n\n- Invalid input'
    const sign = r.simpNum < 0 ? '-' : ''
    const mixedStr = r.mixed.whole !== 0
      ? `${r.mixed.whole} ${r.mixed.num}/${r.mixed.den}`
      : `${sign}${Math.abs(r.simpNum)}/${r.simpDen}`
    return `# Mixed Fraction Calculator\n\n- A = ${a.whole} ${a.num}/${a.den} → ${r.A.num}/${r.A.den}\n- B = ${b.whole} ${b.num}/${b.den} → ${r.B.num}/${r.B.den}\n- Operation: A ${op} B = ${r.num}/${r.den}\n- GCD(${Math.abs(r.num)}, ${r.den}) = ${r.g}\n- Simplified: ${r.simpNum}/${r.simpDen}\n- Mixed: **${mixedStr}**`
  }, [a, b, op, r])

  const Field = ({ label, value, onChange, min }: { label: string, value: number, onChange: (n: number) => void, min?: number }) => (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        onChange={e => onChange(Number.parseInt(e.target.value || '0', 10))}
        className="mt-1 w-full px-3 py-2 rounded-lg bg-[#0a0e1a] border border-[var(--line)] text-white font-mono-math text-base focus:outline-none focus:border-emerald-400/60"
      />
    </label>
  )

  const FractionInput = ({ f, set, label }: { f: MixedFraction, set: (n: MixedFraction) => void, label: string }) => (
    <div className="rounded-xl border border-[var(--line)] bg-white/[0.02] p-4">
      <div className="text-xs uppercase tracking-wider text-emerald-300/80 mb-3">{label}</div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Whole" value={f.whole} onChange={n => set({ ...f, whole: n })} />
        <Field label="Numerator" value={f.num} onChange={n => set({ ...f, num: n })} />
        <Field label="Denominator" value={f.den} onChange={n => set({ ...f, den: Math.max(1, n) })} min={1} />
      </div>
    </div>
  )

  // Pie SVG
  const renderPie = () => {
    const cx = 110, cy = 110, R = 96
    const polarToCart = (angle: number) => [cx + R * Math.cos(angle - Math.PI / 2), cy + R * Math.sin(angle - Math.PI / 2)]
    const slicePath = (i: number, frac: number) => {
      const start = (i / slices) * Math.PI * 2
      const end = ((i + frac) / slices) * Math.PI * 2
      const [sx, sy] = polarToCart(start)
      const [ex, ey] = polarToCart(end)
      const large = end - start > Math.PI ? 1 : 0
      return `M ${cx} ${cy} L ${sx} ${sy} A ${R} ${R} 0 ${large} 1 ${ex} ${ey} Z`
    }
    const items = []
    for (let i = 0; i < slices; i += 1) {
      const filled = i < fullFilled ? 1 : (i === fullFilled ? partial : 0)
      if (filled > 0) {
        items.push(<path key={`f${i}`} d={slicePath(i, filled)} fill="#38e1a4" opacity={0.85} />)
      }
      items.push(<path key={`b${i}`} d={slicePath(i, 1)} fill="none" stroke="#1e293b" strokeWidth={1} />)
    }
    return items
  }

  return (
    <SolverCard
      ref={ref}
      id="fraction"
      eyebrow="Fractions · 22,200/mo"
      title="Mixed Fraction Calculator: Add, Subtract & Multiply Fractions"
      volume="Vol 22.2K"
      highlighted={highlighted}
    >
      <p className="text-slate-400 text-sm md:text-[15px] leading-relaxed mb-6">
        A <strong className="text-slate-200">mixed fraction</strong> like{' '}
        <span className="font-mono-math text-emerald-300">2 1/3</span> is converted to an{' '}
        <strong className="text-slate-200">improper fraction</strong> via{' '}
        <span className="font-mono-math text-emerald-300">(whole × den + num) / den</span>. The solver finds a common denominator, performs
        the operation, and simplifies using the <strong className="text-slate-200">GCD</strong> — every step shown transparently below.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FractionInput f={a} set={setA} label="Fraction A" />
          <div className="flex items-center justify-center gap-3">
            <span className="text-slate-500 text-sm">Operator</span>
            <select
              value={op}
              onChange={e => setOp(e.target.value as Op)}
              className="px-4 py-2 rounded-lg bg-[#0a0e1a] border border-[var(--line)] text-white font-mono-math focus:outline-none focus:border-emerald-400/60"
            >
              <option value="+">+</option>
              <option value="-">−</option>
              <option value="*">×</option>
              <option value="/">÷</option>
            </select>
          </div>
          <FractionInput f={b} set={setB} label="Fraction B" />

          <div className="space-y-2 pt-2">
            <div className="text-xs uppercase tracking-wider text-slate-400">Step-by-Step Solver</div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">1.</span> A = ({a.whole}·{a.den} + {a.num})/{a.den} = {r.A.num}/{r.A.den}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">2.</span> B = ({b.whole}·{b.den} + {b.num})/{b.den} = {r.B.num}/{r.B.den}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">3.</span> A {op} B = {r.num}/{r.den}
            </div>
            <div className="rounded-lg border border-[var(--line)] bg-white/[0.02] px-3.5 py-2.5 font-mono-math text-sm text-slate-200">
              <span className="text-slate-500">4.</span> GCD({Math.abs(r.num)}, {r.den}) = {r.g}
            </div>
            <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/[0.06] px-3.5 py-2.5 font-mono-math text-sm text-emerald-200">
              <span className="text-emerald-400/80">=</span>{' '}
              {r.simpNum}/{r.simpDen}
              {r.mixed.whole !== 0 && r.mixed.num !== 0 && (
                <span className="text-slate-400"> · or · {r.mixed.whole} {r.mixed.num}/{r.mixed.den}</span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-2">
              <CopyButton markdown={md} />
              <EmailSolutionButton title="Mixed Fraction Calculator" markdown={md} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--line)] bg-[#0a0e1a] p-4 blueprint-grid">
          <div className="absolute-bg-only flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            <Pizza className="w-3 h-3" /> Pizza Slicing Board
          </div>
          <svg viewBox="0 0 220 220" className="w-full max-w-xs mx-auto">
            <circle cx={110} cy={110} r={98} fill="#0e1422" stroke="#1e293b" />
            {renderPie()}
            <circle cx={110} cy={110} r={4} fill="#fbbf24" />
          </svg>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2">
              <div className="text-slate-500">Slices</div>
              <div className="font-mono-math text-slate-100">{slices}</div>
            </div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2">
              <div className="text-slate-500">Filled</div>
              <div className="font-mono-math text-emerald-300">{filledFloat.toFixed(2)}</div>
            </div>
            <div className="rounded-md bg-white/[0.02] border border-[var(--line)] py-2">
              <div className="text-slate-500">Decimal</div>
              <div className="font-mono-math text-slate-100">{pieValue.toFixed(3)}</div>
            </div>
          </div>
        </div>
      </div>
    </SolverCard>
  )
})

FractionCalculator.displayName = 'FractionCalculator'
export default FractionCalculator
